import { createApiClient } from './axios-instance';
import { BaseResponse } from './auth.service';

// Types

/**
 * Business domain category for marketplace grouping.
 * Mirror PortalAppCategory (libs/backend-shared/.../portal/app.py).
 */
export type AppCategory =
  | 'finance'
  | 'marketing_sales'
  | 'hr'
  | 'operations'
  | 'supply_chain'
  | 'uncategorized';

export const APP_CATEGORY_LABELS: Record<AppCategory, string> = {
  finance: 'Tài chính – Kế toán',
  marketing_sales: 'Marketing – Bán hàng',
  hr: 'Quản trị nguồn nhân lực',
  operations: 'Quản lý – Điều hành',
  supply_chain: 'Sản xuất – Cung ứng',
  uncategorized: 'Chưa phân loại',
};

/**
 * Render order for marketplace sections. Categorized buckets first,
 * "Chưa phân loại" last.
 */
export const APP_CATEGORY_ORDER: AppCategory[] = [
  'finance',
  'marketing_sales',
  'hr',
  'operations',
  'supply_chain',
  'uncategorized',
];

export interface App {
  id: string;
  slug: string;
  name: string;
  description: string;
  is_active: boolean;
  is_beta: boolean;
  category: AppCategory;
  version: string;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceSubscription {
  workspace_id: string;
  app_id: string;
  subscribed_by: string;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceSubscriptionWithApp extends WorkspaceSubscription {
  app_slug: string;
  app_name: string;
  app_description: string;
  app_is_beta: boolean;
  app_category: AppCategory;
  app_version: string;
}

export interface SubscriptionCreateRequest {
  app_id: string;
}

// API Service
class AppService {
  // Get apps accessible to the current user in a workspace
  async getApps(idToken: string, workspaceId: string): Promise<App[]> {
    const client = createApiClient(idToken);
    const response = await client.get<BaseResponse<App[]>>('/apps/', {
      params: { workspace_id: workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get apps');
    }

    return response.data.data || [];
  }

  // Get the public marketplace catalog (no workspace context).
  // Used by onboarding flows that render the app picker before any
  // workspace exists.
  async getAppCatalog(idToken: string): Promise<App[]> {
    const client = createApiClient(idToken);
    const response = await client.get<BaseResponse<App[]>>('/apps/');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get app catalog');
    }

    return response.data.data || [];
  }

  // Get workspace subscriptions
  async getWorkspaceSubscriptions(
    workspaceId: string,
    idToken: string,
    all: boolean = false
  ): Promise<WorkspaceSubscriptionWithApp[]> {
    const client = createApiClient(idToken);
    const params = all ? { all: true } : {};
    const response = await client.get<BaseResponse<WorkspaceSubscriptionWithApp[]>>(
      `/workspaces/${workspaceId}/subscriptions`,
      { params }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get workspace subscriptions');
    }

    return response.data.data || [];
  }

  // Subscribe workspace to app
  async subscribeWorkspaceToApp(
    workspaceId: string,
    appId: string,
    idToken: string
  ): Promise<WorkspaceSubscription> {
    const client = createApiClient(idToken);
    const response = await client.post<BaseResponse<WorkspaceSubscription>>(
      `/workspaces/${workspaceId}/subscriptions`,
      {
        app_id: appId,
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to subscribe workspace to app');
    }

    return response.data.data!;
  }

  // Unsubscribe workspace from app
  async unsubscribeWorkspaceFromApp(
    workspaceId: string,
    appId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken);
    const response = await client.delete<BaseResponse<null>>(
      `/workspaces/${workspaceId}/subscriptions/${appId}`
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unsubscribe workspace from app');
    }
  }

  // Get user's accessible apps across all workspaces
  async getUserAccessibleApps(idToken: string): Promise<WorkspaceSubscriptionWithApp[]> {
    const client = createApiClient(idToken);
    const response = await client.get<BaseResponse<WorkspaceSubscriptionWithApp[]>>('/my-apps');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get user accessible apps');
    }

    return response.data.data || [];
  }

  // Check if current user has access to a specific app in a workspace
  async checkAppAccess(
    workspaceId: string,
    appSlug: string,
    idToken: string
  ): Promise<{ has_access: boolean }> {
    const client = createApiClient(idToken);
    const response = await client.get<BaseResponse<{ has_access: boolean }>>(
      `/workspaces/${workspaceId}/apps/${appSlug}/access`
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to check app access');
    }

    return response.data.data || { has_access: false };
  }
}

export const appService = new AppService();
